// apps/client/src/components/layout/AppLayout.tsx
// Main layout with morphing sidebar and unified top bar

import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Outlet, useOutletContext, Navigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { collection, getDocs, query, where, documentId } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../lib/firebase';
import MorphingSidebar from './MorphingSidebar';
import UnifiedTopBar from './UnifiedTopBar';
import { CommandPaletteProvider, CommandPalette } from '../command-palette';
import { useUserData } from '../../hooks/useUserData';
import { useCompanyData } from '../../hooks/useCompanyData';
import Loading from '../common/Loading';
import BrokerDataAttestationModal from '../modals/BrokerDataAttestationModal';
import { useHideCommissions } from '../../hooks/useHideCommissions';
import SubscriptionStateGate from '../common/SubscriptionStateGate';
import ViewAsBanner from '../common/ViewAsBanner';
import { useAuth } from '../../context/AuthContext';
import { Menu } from 'lucide-react';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { useLoanBooks } from '../../hooks/useLoanBooks';
import { adminPathDenied } from '../../config/adminAccess';

// Sidebar storage keys
const SIDEBAR_MODE_KEY = 'valutten-sidebar-mode';
const SIDEBAR_EXPANDED_KEY = 'valutten-sidebar-expanded';
const COLLAPSE_DELAY = 300;

// Define AppContext types based on what the hooks return
export interface AppContextType {
  userData: any | null;
  companyData: any | null;
  offices: any[];
  teamMembers: any[];
  userVisibleOffices: any[];
  userVisibleTemplates: any[];
  loanBooks: ResolvedLoanBook[];
  loanBooksLoading: boolean;
  refreshLoanBooks: () => void;
}

export const AppLayout: React.FC = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const [allTemplates, setAllTemplates] = useState<any[]>([]);
  const [showAttestation, setShowAttestation] = useState(false);
  const [brokerStats, setBrokerStats] = useState<any>(null);
  const hideCommissions = useHideCommissions();

  // Sidebar state management (lifted from MorphingSidebar)
  const collapseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [sidebarMode, setSidebarMode] = useState<'hover' | 'locked'>(() => {
    const saved = localStorage.getItem(SIDEBAR_MODE_KEY);
    return saved === 'locked' ? 'locked' : 'hover';
  });

  const [isManuallyExpanded, setIsManuallyExpanded] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_EXPANDED_KEY);
    return saved !== 'false'; // Default to expanded
  });

  const [isHovered, setIsHovered] = useState(false);

  // Calculate if sidebar is expanded based on mode
  const isSidebarExpanded = sidebarMode === 'hover' ? isHovered : isManuallyExpanded;

  const { userData: rawUserData, loading: userLoading } = useUserData();
  const { viewAsActive, viewAsTargetCompanyId, companyId: claimsCompanyId, isFounder } = useAuth();
  // Founder view-as: synthesize a userData whose companyId points at the
  // viewAs target, so all downstream consumers (DashboardPage data hooks,
  // RequireRole guards reading via outlet context, etc.) query the target
  // tenant's data instead of the founder's own 'system' company. The
  // founder's identity (uid, email, displayName) is preserved — only
  // companyId / role / canSeeAllOffices are swapped to mirror the auth-claim
  // override done in AuthContext. Non-viewAs sessions are unchanged.
  const userData = useMemo(() => {
    if (!rawUserData) return rawUserData;
    if (!viewAsActive || !viewAsTargetCompanyId) return rawUserData;
    return {
      ...rawUserData,
      companyId: viewAsTargetCompanyId,
      role: 'super_admin',
      canSeeAllOffices: true,
      assignedOffices: rawUserData.assignedOffices ?? [],
    };
  }, [rawUserData, viewAsActive, viewAsTargetCompanyId]);
  // Suppress an unused-var lint in case useAuth's other returns get pruned.
  void claimsCompanyId;
  void isFounder;
  const { companyData, offices, teamMembers, loading: companyLoading } = useCompanyData();

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_MODE_KEY, sidebarMode);
  }, [sidebarMode]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_EXPANDED_KEY, String(isManuallyExpanded));
  }, [isManuallyExpanded]);

  // Cleanup collapse timeout on unmount
  useEffect(() => {
    return () => {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
      }
    };
  }, []);

  // Sidebar handlers
  const handleSidebarMouseEnter = useCallback(() => {
    if (sidebarMode === 'hover') {
      if (collapseTimeoutRef.current) {
        clearTimeout(collapseTimeoutRef.current);
        collapseTimeoutRef.current = null;
      }
      setIsHovered(true);
    }
  }, [sidebarMode]);

  const handleSidebarMouseLeave = useCallback(() => {
    if (sidebarMode === 'hover') {
      collapseTimeoutRef.current = setTimeout(() => {
        setIsHovered(false);
      }, COLLAPSE_DELAY);
    }
  }, [sidebarMode]);

  const handlePinSidebar = useCallback(() => {
    setSidebarMode('locked');
    setIsManuallyExpanded(true);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    if (sidebarMode === 'locked') {
      setIsManuallyExpanded(!isManuallyExpanded);
    } else {
      // In hover mode, clicking toggle pins it open
      setSidebarMode('locked');
      setIsManuallyExpanded(true);
    }
  }, [sidebarMode, isManuallyExpanded]);

  const handleEnableHoverMode = useCallback(() => {
    setSidebarMode('hover');
    setIsHovered(false);
  }, []);

  // Source-of-truth: companies/{id}.enabledTemplateIds. We fetch ONLY those
  // fileTemplate docs by ID so the query satisfies the Phase 1 strict-read
  // rule on /fileTemplates (firestore.rules:1025-1031, PROD-live 2026-04-24).
  // The previous unfiltered `query(collection(db, 'fileTemplates'))` returned
  // permission-denied for every non-super-admin user (rules are not filters
  // — any unreadable doc in the result set fails the whole query) and was
  // swallowed silently, leaving allTemplates=[] for brokers and breaking
  // every page that depended on userVisibleTemplates downstream.
  useEffect(() => {
    const enabledIds: string[] | undefined = companyData?.enabledTemplateIds;
    if (!enabledIds || enabledIds.length === 0) {
      setAllTemplates([]);
      return;
    }

    const fetchTemplates = async () => {
      try {
        // Firestore `in` operator caps at 30 IDs per query — batch.
        const batches: string[][] = [];
        for (let i = 0; i < enabledIds.length; i += 30) {
          batches.push(enabledIds.slice(i, i + 30));
        }
        const snapshots = await Promise.all(
          batches.map((batch) =>
            getDocs(query(collection(db, 'fileTemplates'), where(documentId(), 'in', batch)))
          )
        );
        const fetchedTemplates = snapshots.flatMap((snap) =>
          snap.docs.map((doc) => ({ id: doc.id, ...(doc.data() as Record<string, any>) }))
        );
        setAllTemplates(fetchedTemplates);
      } catch (e: any) {
        // Surface the failure — the previous "silent fail" is what hid the
        // Phase 1 strict-read regression from 2026-04-24 to 2026-04-28.
        console.error('[AppLayout] fetchTemplates failed', e);
      }
    };
    fetchTemplates();
  }, [companyData?.enabledTemplateIds]);

  const userVisibleOffices = useMemo(() => {
    if (!userData || !offices) return [];
    let visible: any[];
    if (userData.role === 'super_admin' || userData.canSeeAllOffices === true) {
      visible = offices;
    } else if (
      (userData.role === 'office_admin' || userData.role === 'administrator') &&
      Array.isArray(userData.assignedOffices)
    ) {
      const assignedIds = new Set(userData.assignedOffices);
      visible = offices.filter((office: any) => assignedIds.has(office.id));
    } else if ((userData.role === 'broker' || userData.role === 'credit_analyst' || userData.role === 'broker_assistant' || userData.role === 'marketing') && Array.isArray(userData.assignedOffices)) {
      const assignedIds = new Set(userData.assignedOffices);
      visible = offices.filter((office: any) => assignedIds.has(office.id));
    } else {
      visible = [];
    }
    // Sort: HQ first, then alphabetical by name
    return visible.sort((a: any, b: any) => {
      if (a.isHeadOffice && !b.isHeadOffice) return -1;
      if (!a.isHeadOffice && b.isHeadOffice) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [userData, offices]);

  // Compute company-enabled templates
  const companyEnabledTemplates = useMemo(() => {
    if (!companyData?.enabledTemplateIds || !allTemplates?.length) return [];
    const enabledIds = new Set(companyData.enabledTemplateIds);
    return allTemplates.filter((t: any) => enabledIds.has(t.id));
  }, [allTemplates, companyData?.enabledTemplateIds]);

  // Office-scoped template IDs from BigQuery (for office_admin/broker)
  const [officeTemplateIds, setOfficeTemplateIds] = useState<string[] | null>(null);

  useEffect(() => {
    const isRestricted = userData?.role === 'office_admin' || userData?.role === 'broker' || userData?.role === 'broker_assistant' || userData?.role === 'credit_analyst' || userData?.role === 'marketing';
    if (!isRestricted || !userData?.companyId) {
      setOfficeTemplateIds(null);
      return;
    }
    const fetchOfficeTemplates = async () => {
      try {
        const fn = httpsCallable(functions, 'getOfficeTemplates');
        const result = await fn({ companyId: userData.companyId });
        setOfficeTemplateIds(result.data as string[] | null);
      } catch (err) {
        console.error('[AppLayout] Failed to fetch office templates:', err);
        setOfficeTemplateIds(null); // Fallback: show all company templates
      }
    };
    fetchOfficeTemplates();
  }, [userData?.role, userData?.companyId]);

  // Final userVisibleTemplates: company-enabled, filtered by explicit admin grant OR office scope
  const userVisibleTemplates = useMemo(() => {
    let templates = companyEnabledTemplates;
    const hasExplicitGrant =
      userData?.allowedTemplateIds &&
      Array.isArray(userData.allowedTemplateIds) &&
      userData.allowedTemplateIds.length > 0;

    if (hasExplicitGrant) {
      // Admin explicitly set allowedTemplateIds — use as sole filter.
      // This overrides the implicit officeTemplateIds heuristic because the admin
      // may grant access to acquired book templates where the broker has no personal data.
      const allowedSet = new Set(userData.allowedTemplateIds);
      templates = templates.filter((t: any) => allowedSet.has(t.id));
    } else if (officeTemplateIds) {
      // No explicit grant — fall back to implicit office-scoped filtering
      const officeSet = new Set(officeTemplateIds);
      templates = templates.filter((t: any) => officeSet.has(t.id));
    }
    return templates;
  }, [companyEnabledTemplates, officeTemplateIds, userData?.allowedTemplateIds]);

  // Loan books — first-class abstraction (resolves both template-based and entity-based)
  const { enabledLoanBooks: loanBooks, loading: loanBooksLoading, refresh: refreshLoanBooks } = useLoanBooks({
    companyId: userData?.role === 'service_staff' ? undefined : userData?.companyId,
  });

  // Compute hasAcquiredBooks from templates
  const hasAcquiredBooks = useMemo(() => {
    return userVisibleTemplates?.some((t: any) => t.isAcquiredLoanBook === true) || false;
  }, [userVisibleTemplates]);

  // Attestation modal logic
  useEffect(() => {
    if (
      userData?.role === 'broker' &&
      userData?.dataLinkStatus === 'auto_linked' &&
      !userData?.dataLinkConfirmed
    ) {
      const fetchBrokerStats = async () => {
        try {
          const getBrokerStats = httpsCallable(functions, 'getBrokerStats');
          const result = await getBrokerStats({
            brokerName: userData.commissionDataName,
          });
          const stats = result.data as any;
          setBrokerStats(stats);
          setShowAttestation(true);
        } catch (error) {
          console.error('Failed to fetch broker stats:', error);
          setBrokerStats({ totalLoans: 0, totalCommission: 0 });
          setShowAttestation(true);
        }
      };
      fetchBrokerStats();
    }
  }, [userData]);

  const handleAttestationConfirm = async () => {
    try {
      const attestBrokerData = httpsCallable(functions, 'attestBrokerData');
      await attestBrokerData({
        brokerName: userData?.commissionDataName,
      });
      setShowAttestation(false);
    } catch (error) {
      console.error('Attestation failed:', error);
      throw error;
    }
  };

  const handleAttestationDeny = () => {
    toast.error('Please contact support at support@valutten.com to correct your broker data link.', { duration: 6000 });
  };

  // ESC to close mobile menu
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileMenuOpen(false);
    };
    if (isMobileMenuOpen) window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isMobileMenuOpen]);

  const isLoading = userLoading || companyLoading;

  // Normalize subscription tier for command palette
  const subscriptionTier = useMemo(() => {
    const rawTier = companyData?.subscription?.tier?.toLowerCase() || '';
    if (rawTier === 'pro' || rawTier === 'professional') return 'pro' as const;
    if (rawTier === 'enterprise' || rawTier === 'business') return 'enterprise' as const;
    return 'plus' as const;
  }, [companyData?.subscription?.tier]);

  // ============================================
  // SUBSCRIPTION STATE GATE: Handles all subscription states
  // - active / trialing / missing: render normally
  // - onboarding / pending_setup: redirect to /activation (except /upload)
  // - grace_period: render with warning banner
  // - suspended: full-page block
  // - churned: full-page block with reactivation CTA
  // White-glove companies (no subscription doc) are not affected — missing subscription = ALLOW.
  // ============================================
  const subscriptionState = companyData?.subscription?.state;

  if (!isLoading && (subscriptionState === 'suspended' || subscriptionState === 'churned')) {
    return (
      <SubscriptionStateGate
        subscriptionState={subscriptionState}
        subscriptionData={companyData?.subscription}
      >
        {null}
      </SubscriptionStateGate>
    );
  }

  if (!isLoading && userData?.role === 'service_staff' &&
      !['/leads/inbox', '/leads/availability', '/settings'].includes(location.pathname.replace(/\/$/, ''))) {
    return <Navigate to="/leads/inbox" replace />;
  }

  // An administrator denied a page or report is sent to the dashboard, which is
  // never itself denied (only its commission sections are), so this cannot loop.
  if (!isLoading && adminPathDenied(userData, location.pathname)) {
    return <Navigate to="/" replace />;
  }

  return (
    <CommandPaletteProvider>
      <CommandPalette subscriptionTier={subscriptionTier} featureFlags={companyData?.featureFlags} />
      {/*
        `h-full`, not `h-screen`: the shell takes its height from the sized
        wrapper MfaEnforcementGuard always renders around it (AppRoutes: `/` →
        MfaEnforcementGuard → AppLayout, the only render site). With no MFA
        banner that wrapper is exactly 100vh, so this is unchanged; with one it
        is 100vh minus the banner. A hard `h-screen` here would ignore the
        banner and push the bottom of the sidebar and the content scroller off
        the viewport. Pinned by MfaEnforcementGuard.test.tsx ("shell height").
      */}
      <div className="flex h-full bg-gray-900 text-white overflow-hidden">
        {/* Desktop Sidebar */}
        <div className="hidden md:block flex-shrink-0 z-40">
          <MorphingSidebar
            userData={userData}
            companyData={companyData}
            offices={userVisibleOffices}
            allTemplates={allTemplates}
            mode={sidebarMode}
            isExpanded={isSidebarExpanded}
            onMouseEnter={handleSidebarMouseEnter}
            onMouseLeave={handleSidebarMouseLeave}
            onEnableHoverMode={handleEnableHoverMode}
            onPinSidebar={handlePinSidebar}
            onToggleSidebar={handleToggleSidebar}
          />
        </div>

        {/* Mobile menu overlay */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 flex z-50 md:hidden" role="dialog" aria-modal="true">
            <div
              className="fixed inset-0 bg-black/60"
              aria-hidden="true"
              onClick={() => setMobileMenuOpen(false)}
            />
            <div className="relative flex-1 flex flex-col max-w-xs w-full bg-gray-900">
              <MorphingSidebar
                userData={userData}
                companyData={companyData}
                offices={userVisibleOffices}
                allTemplates={allTemplates}
                mode="locked"
                isExpanded={true}
                onMouseEnter={() => {}}
                onMouseLeave={() => {}}
                onEnableHoverMode={() => setMobileMenuOpen(false)}
                onPinSidebar={() => {}}
                onToggleSidebar={() => setMobileMenuOpen(false)}
              />
            </div>
          </div>
        )}

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Mobile header */}
          <div className="md:hidden flex items-center justify-between px-4 py-3 bg-gray-900 border-b border-gray-800">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
            <span className="text-lg font-semibold">VALUTTEN</span>
            <div className="w-10" /> {/* Spacer for balance */}
          </div>

          {/* Unified top bar - context aware */}
          <UnifiedTopBar
            companyTier={subscriptionTier}
            userRole={userData?.role}
            hasAcquiredBooks={hasAcquiredBooks}
            isDemo={!!companyData?.isDemo}
            offices={userVisibleOffices}
            userData={userData}
            betaFeatures={companyData?.betaFeatures || []}
            grantedFeatures={companyData?.featureFlags || {}}
            aggregator={companyData?.aggregator}
          />

          <ViewAsBanner />

          <SubscriptionStateGate
            subscriptionState={subscriptionState}
            subscriptionData={companyData?.subscription}
          >
            {/* Page content. `relative` makes <main> the containing block, so an
                absolutely positioned descendant (e.g. an sr-only label far down a
                long page) scrolls inside it instead of stretching the document
                and exposing a white strip below the shell. */}
            <main className="relative flex-1 overflow-x-hidden overflow-y-auto p-4 md:p-6">
              {isLoading ? (
                <Loading />
              ) : userData?.role === 'broker' && userData?.dataLinkStatus === 'pending_link' ? (
                <div className="flex items-center justify-center min-h-[60vh]">
                  <div className="text-center p-8">
                    <h2 className="text-2xl font-bold mb-4">Account Setup in Progress</h2>
                    <p className="text-gray-400 mb-4">Your broker profile is being linked to your commission data.</p>
                    <p className="text-gray-400">This usually completes within a few minutes. Please refresh the page shortly.</p>
                  </div>
                </div>
              ) : (
                <Outlet context={{ userData, companyData, offices, teamMembers, userVisibleOffices, userVisibleTemplates, loanBooks, loanBooksLoading, refreshLoanBooks }} />
              )}
            </main>
          </SubscriptionStateGate>
        </div>
      </div>

      {/* Broker Data Attestation Modal */}
      {showAttestation && brokerStats && (
        <BrokerDataAttestationModal
          show={showAttestation}
          brokerName={userData?.commissionDataName || ''}
          totalLoans={brokerStats.totalLoans || 0}
          totalCommission={brokerStats.totalCommission || 0}
          hideCommissions={hideCommissions}
          onConfirm={handleAttestationConfirm}
          onDeny={handleAttestationDeny}
        />
      )}
    </CommandPaletteProvider>
  );
};

export function useAppContext() {
  return useOutletContext<AppContextType>();
}
